import { api } from './api';

export interface User {
    user_id: number;
    email: string;
    username?: string;
    display_name?: string;
    account_tier: 'free' | 'premium' | 'pro' | 'admin';
    email_verified: boolean;
    avatar_url?: string;
    preferences?: Record<string, unknown>;
    created_at?: string;
    last_login_at?: string;
}

export interface AuthResponse {
    token: string;
    user: User;
    message?: string;
}

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface RegisterData {
    email: string;
    password: string;
    username?: string;
    display_name?: string;
}

const TOKEN_KEY = 'imperialismmaps_token';

export const authService = {
    setToken(token: string): void {
        localStorage.setItem(TOKEN_KEY, token);
    },

    getToken(): string | null {
        return localStorage.getItem(TOKEN_KEY);
    },

    removeToken(): void {
        localStorage.removeItem(TOKEN_KEY);
    },

    isAuthenticated(): boolean {
        return this.getToken() !== null;
    },

    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const response = await api.post<AuthResponse>('/auth/login', credentials);

        if (!response.success || !response.data) {
            throw new Error(response.error || 'Login failed');
        }

        const { token, user } = response.data;
        this.setToken(token);

        return { token, user };
    },

    async register(data: RegisterData): Promise<AuthResponse> {
        const response = await api.post<AuthResponse>('/auth/register', data);

        if (!response.success || !response.data) {
            throw new Error(response.error || 'Registration failed');
        }

        const { token, user } = response.data;
        this.setToken(token);

        return { token, user };
    },

    async getCurrentUser(): Promise<User> {
        const token = this.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await api.get<{ user: User }>('/auth/me');

        if (!response.success || !response.data) {
            this.removeToken();
            throw new Error(response.error || 'Failed to get user information');
        }

        return response.data.user;
    },

    async verifyToken(): Promise<boolean> {
        const token = this.getToken();
        if (!token) {
            return false;
        }

        try {
            const response = await api.post<{ valid: boolean; user: User }>('/auth/verify', {});
            return response.success && response.data?.valid === true;
        } catch {
            return false;
        }
    },

    logout(): void {
        this.removeToken();
    },

    getAuthHeader(): Record<string, string> {
        const token = this.getToken();
        if (!token) {
            return {};
        }
        return {
            Authorization: `Bearer ${token}`,
        };
    },
};
